export type MembershipTab = 'members' | 'tiers' | 'activity' | 'pending';

export type MembershipRouteMatch =
  | { kind: 'overview'; tab: MembershipTab }
  | { kind: 'create' }
  | { kind: 'edit'; typeUuid: string }
  | { kind: 'customer'; membershipUuid: string }
  | null;

const MEMBERSHIP_BASE_PATH = '/store/memberships';
const MEMBERSHIP_TAB_VALUES = new Set<MembershipTab>(['members', 'tiers', 'activity', 'pending']);

const isTab = (value: string): value is MembershipTab =>
  (MEMBERSHIP_TAB_VALUES as Set<string>).has(value);

export const resolveMembershipRoute = (route: string): MembershipRouteMatch => {
  const pathOnly = String(route || '').split('?')[0];

  if (pathOnly === MEMBERSHIP_BASE_PATH) {
    return { kind: 'overview', tab: 'members' };
  }

  if (pathOnly === `${MEMBERSHIP_BASE_PATH}/new`) {
    return { kind: 'create' };
  }

  const editMatch = pathOnly.match(/^\/store\/memberships\/([^/]+)\/edit$/);
  if (editMatch) {
    return { kind: 'edit', typeUuid: editMatch[1] };
  }

  const customerMatch = pathOnly.match(/^\/store\/memberships\/customers\/([^/?]+)$/);
  if (customerMatch) {
    return { kind: 'customer', membershipUuid: customerMatch[1] };
  }

  const tabMatch = pathOnly.match(/^\/store\/memberships\/([^/]+)$/);
  if (tabMatch && isTab(tabMatch[1])) {
    return { kind: 'overview', tab: tabMatch[1] };
  }

  return null;
};

export const resolveLegacyMembershipRedirect = (route: string): string | null => {
  const [pathOnly, search = ''] = String(route || '').split('?');

  // Legacy /store?tab=... → new path-based memberships routes.
  if (pathOnly === '/store') {
    const params = new URLSearchParams(search);
    const tab = String(params.get('tab') || '').toLowerCase();

    if (tab === 'memberships') {
      params.delete('tab');
      const nextSearch = params.toString();
      return nextSearch ? `${MEMBERSHIP_BASE_PATH}?${nextSearch}` : MEMBERSHIP_BASE_PATH;
    }

    if (!isTab(tab)) {
      return null;
    }

    params.delete('tab');
    const nextSearch = params.toString();
    const base = `${MEMBERSHIP_BASE_PATH}/${tab}`;
    return nextSearch ? `${base}?${nextSearch}` : base;
  }

  // Legacy /store/memberships?tab=... → /store/memberships/<tab>.
  if (pathOnly === MEMBERSHIP_BASE_PATH) {
    const params = new URLSearchParams(search);
    const tab = String(params.get('tab') || '').toLowerCase();
    if (!isTab(tab)) {
      return null;
    }
    params.delete('tab');
    const nextSearch = params.toString();
    const base = `${MEMBERSHIP_BASE_PATH}/${tab}`;
    return nextSearch ? `${base}?${nextSearch}` : base;
  }

  return null;
};
